import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Style from './marketplace.module.css'
import IntegrationBuilder from './Components/IntegrationBuilder'
import SystemGrid from './Components/SystemGrid'
import useIntegrationBuilder from './useIntegrationBuilder'
import SearchBar from '../../SharedComponents/SearchBar/SearchBar'
import CategoryFilter from '../../SharedComponents/CategoryFilter/CategoryFilter'
import ConnectorSteps, { CONNECTOR_STEP } from '../../SharedComponents/ConnectorSteps/ConnectorSteps'
import { useGetMarketplaceEndpointsQuery } from '../../../features/marketplace/marketplaceApi'
import { useGetConnectionsQuery } from '../../../features/connections/connectionsApi'
import { CATEGORIES, describeSystem, matchesSearch } from '../../../features/marketplace/connectorCatalog'

const ALL = 'all'
const EMPTY = []

const bySystemOrder = (a, b) => (Number(b.isAvailable) - Number(a.isAvailable)) || a.name.localeCompare(b.name)

const MarketPlace = () => {
    const { data: endpoints = EMPTY, isLoading, isError, refetch } = useGetMarketplaceEndpointsQuery()
    const { data: connections = EMPTY } = useGetConnectionsQuery()
    const [search, setSearch] = useState('')
    const [category, setCategory] = useState(ALL)

    const systems = useMemo(() => endpoints.map(describeSystem).sort(bySystemOrder), [endpoints])
    const ownedShortNames = useMemo(
        () => new Set(connections.flatMap((connection) => [connection.source.shortName, connection.target.shortName])),
        [connections]
    )
    const builder = useIntegrationBuilder(systems)
    const requestedSystem = new URLSearchParams(useLocation().search).get('system')
    const appliedSystem = useRef(null)

    useEffect(() => {
        if (!requestedSystem || appliedSystem.current === requestedSystem) return
        const system = systems.find((item) => String(item.id) === requestedSystem)
        if (system) {
            appliedSystem.current = requestedSystem
            if (!builder.first || builder.first.id !== system.id) builder.select(system)
        }
    }, [requestedSystem, systems, builder])

    const searched = systems.filter((system) => matchesSearch(system, search))
    const visible = category === ALL ? searched : searched.filter((system) => system.category === category)
    const groups = CATEGORIES
        .map((info) => ({ ...info, systems: visible.filter((system) => system.category === info.key) }))
        .filter((group) => group.systems.length > 0)
    const featured = !search && category === ALL ? systems.filter((system) => system.isFeatured) : EMPTY

    const filterOptions = [
        { key: ALL, label: 'All systems', count: searched.length },
        ...CATEGORIES
            .map((info) => ({ key: info.key, label: info.label, count: searched.filter((system) => system.category === info.key).length }))
            .filter((option) => option.count > 0 || option.key === category)
    ]

    const renderCatalog = () => {
        if (isLoading) {
            return (
                <div className={Style.grid} aria-busy="true">
                    {Array.from({ length: 8 }).map((_, index) => <span key={index} className={Style.skeleton} />)}
                </div>
            )
        }
        if (isError) {
            return (
                <div className={Style.stateMessage} role="alert">
                    The systems could not be loaded.
                    <button type="button" className={Style.textButton} onClick={refetch}>Try again</button>
                </div>
            )
        }
        if (visible.length === 0) {
            return (
                <div className={Style.stateMessage}>
                    {search ? `No systems match "${search}".` : 'No systems in this category yet.'}
                    <button type="button" className={Style.textButton} onClick={() => { setSearch(''); setCategory(ALL) }}>
                        Show all systems
                    </button>
                </div>
            )
        }
        return (
            <>
                {featured.length > 0 ? (
                    <SystemGrid
                        title="Featured"
                        description="Picked by i95Dev as common starting points."
                        systems={featured}
                        showDescriptions
                        builder={builder}
                        ownedShortNames={ownedShortNames}
                    />
                ) : null}
                {groups.map((group) => (
                    <SystemGrid
                        key={group.key}
                        title={group.label}
                        description={group.description}
                        systems={group.systems}
                        builder={builder}
                        ownedShortNames={ownedShortNames}
                    />
                ))}
            </>
        )
    }

    return (
        <div className={`main-content ${Style.stickyHost}`}>
            <div className="page-content">
                <div className={`container-fluid ${Style.page}`}>
                    <header className={Style.header}>
                        <div>
                            <h1 className={Style.title}>Connect your systems</h1>
                            <p className={Style.subtitle}>
                                Select the applications you want to integrate and move data between your ERP and eCommerce systems.
                            </p>
                        </div>
                        <ConnectorSteps current={CONNECTOR_STEP.CHOOSE} />
                    </header>

                    <IntegrationBuilder builder={builder} />

                    <div className={Style.toolbar}>
                        <SearchBar
                            className={Style.search}
                            value={search}
                            onChange={setSearch}
                            placeholder="Search by name, vendor or type"
                            label="Search systems"
                        />
                        <CategoryFilter options={filterOptions} value={category} onChange={setCategory} label="System categories" />
                    </div>

                    {renderCatalog()}
                </div>
            </div>
        </div>
    )
}

export default MarketPlace
